import { useMemo, useState } from "react";
import styled from "styled-components";
import { fonts } from "../../../styles/theme";
import { Barra, Card, Chip, Chips, Etiqueta, Meta, Numero, Pilula, cores } from "../../relatorio/styles";
import { NumeroAnimado } from "../../relatorio/componentes/numero-animado";
import {
  FILTROS_DE_CONTA,
  descricaoDoConvite,
  filtrarContas,
  iniciais,
  problemasDaConta,
  resumoDasContas,
  situacaoDaConta,
  type FiltroDeConta,
} from "../pages/contas";
import { desdeQuando } from "../pages/estados";
import type { ContaConectada } from "../types";

const Secao = styled.section`
  font-family: ${fonts.gilroyRegular}, system-ui, sans-serif;
  color: ${cores.ink};
  display: flex;
  flex-direction: column;
  gap: 18px;
  margin-bottom: 36px;

  h2 {
    font-family: ${fonts.gilroySemiBold}, sans-serif;
    font-size: 22px;
    letter-spacing: -0.02em;
    margin: 0;
  }
`;

const Kpis = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  @media (min-width: 900px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 16px;
  }
`;

const Kpi = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 18px 20px;
`;

const Rotulo = styled(Meta)`
  font-family: ${fonts.gilroySemiBold}, sans-serif;
  color: ${cores.ink3};
  font-size: 13px;
`;

const Barra_ = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;

  input {
    flex: 1;
    min-width: 220px;
    border: 1px solid ${cores.line};
    border-radius: 1000px;
    padding: 11px 18px;
    font: inherit;
    font-size: 14px;
    outline: none;
    background: #fff;
  }
`;

const Linha = styled.div`
  display: grid;
  grid-template-columns: 44px 1fr;
  gap: 14px;
  padding: 16px 0;
  border-top: 1px solid ${cores.line};
  &:first-child {
    border-top: 0;
  }
  @media (min-width: 900px) {
    grid-template-columns: 44px 1.4fr 1fr auto;
    align-items: center;
  }
`;

const Avatar = styled.div<{ $cor: string; $fundo: string }>`
  width: 44px;
  height: 44px;
  border-radius: 999px;
  background: ${({ $fundo }) => $fundo};
  color: ${({ $cor }) => $cor};
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${fonts.gilroySemiBold}, sans-serif;
  font-size: 14px;
`;

const Botaozinho = styled.button`
  border: 1px solid ${cores.line};
  background: #fff;
  color: ${cores.blue};
  font-family: ${fonts.gilroySemiBold}, sans-serif;
  font-size: 13px;
  border-radius: 1000px;
  padding: 8px 14px;
  cursor: pointer;
  white-space: nowrap;
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const CORES_KPI = {
  verde: { fundo: "#E6F4EA", cor: "#188038" },
  vermelha: { fundo: "#FCE8E6", cor: "#D93025" },
  amarela: { fundo: "#FEF7E0", cor: "#E37400" },
  azul: { fundo: "#E8F0FE", cor: "#1A73E8" },
  cinza: { fundo: cores.grayTint, cor: cores.ink3 },
} as const;

type Props = {
  contas: ContaConectada[];
  carregando: boolean;
  verificando: number | "todas" | null;
  onVerificar: (userId: number) => void;
  onVerificarTodas: () => void;
};

export const ContasConectadas = ({ contas, carregando, verificando, onVerificar, onVerificarTodas }: Props) => {
  const [filtro, setFiltro] = useState<FiltroDeConta>("TODAS");
  const [busca, setBusca] = useState("");
  const [aberta, setAberta] = useState<number | null>(null);
  const resumo = useMemo(() => resumoDasContas(contas), [contas]);
  const visiveis = useMemo(() => filtrarContas(contas, filtro, busca), [contas, filtro, busca]);
  const ultimaChecagem = useMemo(
    () => contas.map((c) => c.invitationsCheckedAt).filter(Boolean).sort().at(-1) ?? null,
    [contas],
  );

  return (
    <Secao aria-label="Contas conectadas">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 16, flexWrap: "wrap" }}>
        <div>
          <h2>Contas conectadas ao Google</h2>
          <Meta>
            Cada e-mail que administra perfis: se a conexão está de pé, quantos perfis dependem dele e se há convites esperando aceite.
            {ultimaChecagem ? ` Convites conferidos ${desdeQuando(ultimaChecagem)}.` : " Convites ainda não conferidos."}
          </Meta>
        </div>
        <Pilula
          as="button"
          type="button"
          $tipo="contorno"
          onClick={onVerificarTodas}
          disabled={verificando !== null}
          style={{ padding: "10px 20px", fontSize: 14 }}
        >
          {verificando === "todas" ? "Verificando…" : "Verificar convites em todas"}
        </Pilula>
      </div>

      <Kpis>
        <Kpi $fundo={CORES_KPI.verde.fundo}>
          <Rotulo>Contas conectadas</Rotulo>
          <Numero $tamanho={40} $cor={CORES_KPI.verde.cor}>
            <NumeroAnimado valor={resumo.conectadas} />
          </Numero>
          <Meta>{`de ${contas.length} contas gestoras`}</Meta>
        </Kpi>
        <Kpi $fundo={CORES_KPI.vermelha.fundo}>
          <Rotulo>Precisam reconectar</Rotulo>
          <Numero $tamanho={40} $cor={CORES_KPI.vermelha.cor}>
            <NumeroAnimado valor={resumo.reconectar + resumo.semToken} />
          </Numero>
          <Meta>{resumo.semToken > 0 ? `${resumo.reconectar} com token vencido · ${resumo.semToken} sem conexão` : "token vencido ou revogado"}</Meta>
        </Kpi>
        <Kpi $fundo={CORES_KPI.amarela.fundo}>
          <Rotulo>Convites pendentes</Rotulo>
          <Numero $tamanho={40} $cor={CORES_KPI.amarela.cor}>
            <NumeroAnimado valor={resumo.convites} />
          </Numero>
          <Meta>alguém nos convidou para gerenciar uma ficha</Meta>
        </Kpi>
        <Kpi $fundo={CORES_KPI.azul.fundo}>
          <Rotulo>Perfis administrados</Rotulo>
          <Numero $tamanho={40} $cor={CORES_KPI.azul.cor}>
            <NumeroAnimado valor={resumo.perfis} />
          </Numero>
          <Meta>{`${resumo.comProblema} com problema de acesso`}</Meta>
        </Kpi>
      </Kpis>

      <Barra_>
        <input
          type="search"
          placeholder="Buscar conta por e-mail ou nome"
          aria-label="Buscar conta"
          value={busca}
          onChange={(evento) => setBusca(evento.target.value)}
        />
        <Chips role="tablist" aria-label="Filtro de contas">
          {FILTROS_DE_CONTA.map((item) => (
            <Chip key={item.chave} type="button" role="tab" aria-selected={filtro === item.chave} $ativo={filtro === item.chave} onClick={() => setFiltro(item.chave)}>
              {item.rotulo}
            </Chip>
          ))}
        </Chips>
      </Barra_>

      <Card style={{ padding: "6px 22px" }}>
        {carregando && <Meta style={{ display: "block", padding: "14px 0" }}>Carregando contas…</Meta>}
        {!carregando && visiveis.length === 0 && <Meta style={{ display: "block", padding: "14px 0" }}>Nenhuma conta nesta situação.</Meta>}
        {visiveis.map((conta) => {
          const situacao = situacaoDaConta(conta);
          const tema = CORES_KPI[situacao.cor];
          const problemas = problemasDaConta(conta);
          const expandida = aberta === conta.userId;
          return (
            <div key={conta.userId}>
              <Linha>
                <Avatar $cor={tema.cor} $fundo={tema.fundo}>{iniciais(conta.name, conta.email)}</Avatar>
                <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 4 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontFamily: fonts.gilroySemiBold, fontSize: 15, overflowWrap: "anywhere" }}>{conta.email}</span>
                    <Etiqueta $cor={situacao.cor}>{situacao.rotulo}</Etiqueta>
                    {conta.invitationsPending > 0 && (
                      <Etiqueta $cor="amarela">{`${conta.invitationsPending} ${conta.invitationsPending === 1 ? "convite pendente" : "convites pendentes"}`}</Etiqueta>
                    )}
                  </div>
                  <Meta>
                    {conta.name && conta.name !== conta.email ? `${conta.name} · ` : ""}
                    {conta.tokenSince ? `conectada ${desdeQuando(conta.tokenSince)}` : "nunca conectada"}
                    {conta.invitationsError ? ` · última consulta falhou: ${conta.invitationsError}` : ""}
                  </Meta>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <Meta>{`${conta.profiles} ${conta.profiles === 1 ? "perfil" : "perfis"}`}</Meta>
                    <Meta style={{ color: problemas > 0 ? CORES_KPI.vermelha.cor : cores.ink4, fontFamily: fonts.gilroySemiBold }}>
                      {problemas > 0 ? `${problemas} com problema` : "todos com acesso"}
                    </Meta>
                  </div>
                  <Barra $largura={conta.profiles ? (conta.comAcesso / conta.profiles) * 100 : 0} $cor={CORES_KPI.verde.cor} $atraso={0.2} />
                  <Meta>
                    {[
                      conta.semAcesso > 0 ? `${conta.semAcesso} sem acesso` : null,
                      conta.suspensos > 0 ? `${conta.suspensos} suspensos` : null,
                      conta.copias > 0 ? `${conta.copias} cópias` : null,
                      conta.acessoSolicitado > 0 ? `${conta.acessoSolicitado} acesso solicitado` : null,
                    ]
                      .filter(Boolean)
                      .join(" · ") || `${conta.comAcesso} com acesso`}
                  </Meta>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {conta.invitationsPending > 0 && (
                    <Botaozinho type="button" onClick={() => setAberta(expandida ? null : conta.userId)}>
                      {expandida ? "Ocultar convites" : "Ver convites"}
                    </Botaozinho>
                  )}
                  <Botaozinho type="button" onClick={() => onVerificar(conta.userId)} disabled={verificando !== null || !conta.hasToken}>
                    {verificando === conta.userId ? "Consultando…" : "Verificar convites"}
                  </Botaozinho>
                </div>
              </Linha>
              {expandida && conta.invitations.length > 0 && (
                <div style={{ padding: "0 0 16px 58px", display: "flex", flexDirection: "column", gap: 8 }}>
                  {conta.invitations.map((convite) => (
                    <Card key={convite.invitationName} $fundo={CORES_KPI.amarela.fundo} $compacto style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <span style={{ fontFamily: fonts.gilroySemiBold, fontSize: 14 }}>{descricaoDoConvite(convite)}</span>
                      <Meta>
                        {[convite.targetAddress, convite.accountTitle ? `conta ${convite.accountTitle}` : null, convite.checkedAt ? `visto ${desdeQuando(convite.checkedAt)}` : null]
                          .filter(Boolean)
                          .join(" · ")}
                      </Meta>
                      <Meta>Para aceitar, entre no Perfil da Empresa com este e-mail e aceite o convite.</Meta>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </Card>

      <Meta>
        O que o Google mostra por API: conexão da conta, situação de cada perfil e convites recebidos. Pedidos de propriedade feitos por terceiros e os avisos por e-mail não são expostos pela API — chegam só na caixa de entrada de cada conta.
      </Meta>
    </Secao>
  );
};
